import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, InlineWarning, Panel } from "@/components/common/Primitives";
import { api, apiError } from "@/lib/api";
import { dateTime, num, pct } from "@/lib/format";

const Metric = ({ label, value, tone = "text-slate-900", testid }) => (
  <div className="border border-slate-200 bg-white px-3 py-2" data-testid={testid}>
    <div className={`mono text-[17px] font-semibold leading-none ${tone}`}>{value}</div>
    <div className="label-xs mt-1">{label}</div>
  </div>
);

export const ValuationDiagnostics = () => {
  const [data, setData] = useState(null);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [diag, status] = await Promise.all([
        api.get("/valuation/diagnostics"),
        api.get("/analyses/reprocess-status"),
      ]);
      setData(diag.data);
      setRun(status.data.run);
      return status.data.run;
    } catch (e) {
      setError(apiError(e));
      return null;
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (run?.status !== "RUNNING") return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [run?.status, load]);

  const reprocess = async () => {
    setBusy(true);
    try {
      const { data: started } = await api.post("/analyses/reprocess");
      setRun(started);
      toast.success(`Herberekening gestart voor ${num(started.total)} voertuigen.`);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const running = run?.status === "RUNNING";

  return (
    <Panel
      title="Waarderingsdiagnostiek"
      subtitle="Werking van de generieke matching-engine per merk"
      actions={
        <button
          type="button"
          onClick={reprocess}
          disabled={busy || running}
          data-testid="reprocess-all"
          className="flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:border-blue-400 hover:text-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${running ? "animate-spin" : ""}`} />
          {running ? "Herberekenen…" : "Alles herberekenen"}
        </button>
      }
      testid="valuation-diagnostics"
    >
      {error && (
        <div className="mx-3 mt-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800">
          {error}
        </div>
      )}

      {running && (
        <div className="p-3">
          <InlineWarning tone="info" testid="reprocess-progress">
            Herberekening bezig: {num(run.completed)} / {num(run.total)} voertuigen
            {run.current_label ? ` — nu: ${run.current_label}` : ""}. Verwacht NL, bijdrage, ROI en
            Deal Score worden voor elk voertuig opnieuw bepaald.
          </InlineWarning>
        </div>
      )}

      {!data ? (
        <div className="px-3 py-6 text-[12.5px] text-slate-500">Laden…</div>
      ) : (
        <div className="p-3">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-7">
            <Metric label="Voertuigen" value={num(data.total_vehicles)} testid="diag-total" />
            <Metric
              label="Met Verwacht NL"
              value={`${data.valued_pct}%`}
              tone={data.valued_pct >= 60 ? "text-emerald-700" : "text-amber-700"}
              testid="diag-valued"
            />
            <Metric
              label="Onvoldoende data"
              value={num(data.insufficient)}
              tone={data.insufficient ? "text-amber-700" : "text-slate-900"}
              testid="diag-insufficient"
            />
            <Metric
              label="Indicatief (niveau D / andere uitvoering)"
              value={num(data.indicative)}
              tone={data.indicative ? "text-amber-700" : "text-slate-900"}
              testid="diag-indicative"
            />
            <Metric
              label="Gem. vergelijkers"
              value={data.avg_comparables ?? "—"}
              testid="diag-comparables"
            />
            <Metric
              label="Gem. matchscore"
              value={data.avg_comparison_score ?? "—"}
              testid="diag-score"
            />
            <Metric
              label="Handmatige waardes"
              value={num(data.manual_values)}
              testid="diag-manual"
            />
          </div>

          <div className="mt-3 overflow-x-auto">
            {data.brands.length === 0 ? (
              <EmptyState
                title="Nog geen analyses"
                description="Start een scan om echte advertenties te analyseren."
                testid="diag-empty"
              />
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Merk</th>
                    <th className="text-right">Voertuigen</th>
                    <th className="text-right">Met Verwacht NL</th>
                    <th className="text-right">Succes</th>
                    <th className="text-right">Gem. vergelijkers</th>
                    <th className="text-right">Gem. matchscore</th>
                  </tr>
                </thead>
                <tbody>
                  {data.brands.map((brand) => (
                    <tr key={brand.make} data-testid={`diag-brand-${brand.make}`}>
                      <td className="text-[12.5px] font-semibold">{brand.make}</td>
                      <td className="num text-right">{num(brand.total)}</td>
                      <td className="num text-right">{num(brand.valued)}</td>
                      <td
                        className={`num text-right font-semibold ${
                          brand.valued_pct >= 60 ? "text-emerald-700" : "text-amber-700"
                        }`}
                      >
                        {pct(brand.valued_pct)}
                      </td>
                      <td className="num text-right">{brand.avg_comparables ?? "—"}</td>
                      <td className="num text-right">{brand.avg_score ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <p className="mt-2 text-[11px] text-slate-500">
            Matchniveaus:{" "}
            {Object.entries(data.level_distribution || {})
              .map(([key, count]) => `${key === "None" || key === "geen" ? "geen" : key}: ${count}`)
              .join(" · ") || "—"}
          </p>
          <p className="mt-2 text-[11px] text-slate-500">
            Geleerde woordenschat: {num(data.lexicon?.tokens)} termen,{" "}
            {num(data.lexicon?.identity_tokens)} prijsbepalend,{" "}
            {num(data.lexicon?.generation_tokens)} generatiecodes · bijgewerkt{" "}
            {dateTime(data.lexicon?.built_at)}
            {run?.finished_at ? ` · laatste herberekening ${dateTime(run.finished_at)}` : ""}
          </p>
        </div>
      )}
    </Panel>
  );
};
